import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import Loader from "../../../Layouts/Loader/Loader";
import Button from "../../../components/FormComponents/Button";
import { useConnectorsContext } from "../../../context/ConnectorsContext";
import { toastSuccess } from "../../../utils/toastUtils";
import EmailConnectorRenderer from "./EmailConnectorRenderer";
import { editFolderOrFile } from "../../../utils/treeData/folderEditorUtils";
import { useTreeContext } from "../../../context/TreeDataContext";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";
import useDirtyChecker from "../../../hooks/useDirtyChecker";

const EmailConnector = () => {
  const { connectorId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selectedConnector, setSelectedConnector } = useConnectorsContext();
  const [connectorData, setConnectorData] = useState(null);
  const { connectorsExplorerData, setConnectorsExplorerData } = useTreeContext();
  const [isSaving, setIsSaving] = useState(false);
  const { isDirty, markClean } = useDirtyChecker(connectorData);

  const fetchEmailConnectorById = async (connectorId) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data } = await axiosInstance.get(`/email_connectors/${connectorId}`);
      setConnectorData(data);
    } catch (e) {
      setError("Failed to load connector.");
      handleApiError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEmailConnector = async () => {
    try {
      setIsSaving(true);
      const { message } = await axiosInstance.put(`/email_connectors/${connectorId}`, connectorData);

      // save name in tree
      const newTree = editFolderOrFile(connectorsExplorerData, { id: connectorId }, connectorData.name);
      setConnectorsExplorerData(newTree);
      if (selectedConnector && selectedConnector.id === connectorId) {
        setSelectedConnector({
          ...selectedConnector,
          name: connectorData.name
        });
      }
      markClean();
      toastSuccess(message);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (connectorId) {
      fetchEmailConnectorById(connectorId);
    }
  }, [connectorId]);

  if (isLoading) return <Loader />;
  if (error) {
    return (
      <div className="w_100 h_100 d_flex align_center justify_center flex_column">
        <div>{error}</div>
        <Button onClick={() => fetchEmailConnectorById(connectorId)}>Retry</Button>
      </div>
    );
  }

  return (
    <div className={"w_100"}>
      <GenericActionBar
        path={
          <div>
            <em>Email Connector</em> → <span>{selectedConnector.name}</span>
          </div>
        }
        actions={[
          {
            key: "save",
            icon: "save",
            title: isSaving ? "Saving..." : "Save",
            onClick: isSaving ? undefined : handleSaveEmailConnector
          }
        ]}
        options={{
          save: {
            className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
          }
        }}
      />
      <EmailConnectorRenderer connectorData={connectorData} setConnectorData={setConnectorData} />
    </div>
  );
};

export default EmailConnector;
